import { Form } from '$components'
import { z } from 'zod'
import { createContext, useContext, ReactNode } from 'react'
import { UpdateSizeCategoryFormContent } from './content'
import { UpdateSizeCategoryFormControls } from './controls'
import { FieldErrors } from 'react-hook-form'

export enum Input {
  Name = 'name',
  FromWeightLbs = 'from_weight_lbs',
  ToWeightLbs = 'to_weight_lbs',
}

export const updateSizeCategorySchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.FromWeightLbs]: z.coerce.number({ invalid_type_error: 'From weight is required.' })
    .min(0, { message: 'From weight must be 0 or greater.' }),
  [Input.ToWeightLbs]: z.coerce.number({ invalid_type_error: 'To weight is required.' })
    .min(0, { message: 'To weight must be 0 or greater.' }),
}).refine((data) => data[Input.ToWeightLbs] > data[Input.FromWeightLbs], {
  message: 'To weight must be greater than from weight.',
  path: [Input.ToWeightLbs],
})

export type UpdateSizeCategorySchema = z.infer<typeof updateSizeCategorySchema>

type UpdateSizeCategoryFormContextType = {
  onSubmit: (values: UpdateSizeCategorySchema) => void
  onCancel: () => void
  isLoading?: boolean
  errors?: FieldErrors<UpdateSizeCategorySchema>
}

const UpdateSizeCategoryFormContext = createContext<UpdateSizeCategoryFormContextType | undefined>(undefined)

type UpdateSizeCategoryFormProps = UpdateSizeCategoryFormContextType & {
  children?: ReactNode
  defaultValues?: Partial<UpdateSizeCategorySchema>
}

export function UpdateSizeCategoryForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
  errors,
}: UpdateSizeCategoryFormProps) {
  const context: UpdateSizeCategoryFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <UpdateSizeCategoryFormContext.Provider value={context}>
      <Form<UpdateSizeCategorySchema, typeof updateSizeCategorySchema>
        schema={updateSizeCategorySchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        errors={errors}
      >
        {children}
      </Form>
    </UpdateSizeCategoryFormContext.Provider>
  )
}

export function useUpdateSizeCategoryForm() {
  const context = useContext(UpdateSizeCategoryFormContext)
  if (context === undefined) {
    throw new Error('useUpdateSizeCategoryForm must be used within an UpdateSizeCategoryForm')
  }
  return context
}

UpdateSizeCategoryForm.Content = UpdateSizeCategoryFormContent
UpdateSizeCategoryForm.Controls = UpdateSizeCategoryFormControls
